/**
 * PATH: apps/billing/src/App.tsx
 * PURPOSE: Main application component for Billing Hub.
 *
 * WHY: Billing workflows need the same unified layout and settings experience
 *      as Jobs Hub to avoid fragmentation across modules.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────┐    ┌────────────────┐
 *   │ SSO/Session  │───▶│ Layout Shell │───▶│ Billing Routes  │
 *   └──────────────┘    └──────────────┘    └────────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/ui: DashboardLayout, buildSettingsTabs
 *   - @quotech/auth: useAuth, ProtectedRoute, SessionManager
 *   - Billing navigation config + pages
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { 
  DashboardLayout, 
  TabBar,
  type User,
  buildSettingsTabs,
  getHubBreadcrumbs,
  useGlobalSearchBridge,
  useTabNavigation,
  useTabKeyboardShortcuts,
  ErrorBoundary,
  VersionWatcher,
} from '@quotech/ui';
import { useApiClient } from '@quotech/api';
import { 
  ProtectedRoute, 
  SSOHandler, 
  useAuth,
  HubGuard,
  startSessionMonitoring,
  stopSessionMonitoring,
  syncIntegrationStatus,
  UnifiedSSOPage,
  useAuthStore,
  getRefreshCookie,
  useComputedIsAdmin,
  useRBAC,
} from '@quotech/auth';
import {
  NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
  MODULE_CONFIG,
  EXTERNAL_LINKS,
  COMMANDS,
  HUB_LINKS,
} from './config/navigation';
import { BILLING_TAB_CONFIG, useBillingTabStore } from './config/tabs';

// Page components
import DashboardPage from './pages/DashboardPage';
import BillsPage from './pages/BillsPage';
import BillDetailPage from './pages/BillDetailPage';
import ApprovalsPage from './pages/ApprovalsPage';
import BookkeepingPage from './pages/BookkeepingPage';
import PaymentSchedulePage from './pages/PaymentSchedulePage';
import SSOPage from './pages/SSOPage';

/**
 * Layout wrapper using @quotech/ui DashboardLayout
 */
function BillingLayout() {
  const { user, logout, organisationId, organisationName } = useAuth();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const apiClient = useApiClient();

  // Build breadcrumbs from path
  const breadcrumbs = getHubBreadcrumbs('billing', location.pathname);
  
  // Global Search Bridge - provides API client with auth for search to work
  const tokenProvider = useMemo(() => ({
    getAccessToken: () => useAuthStore.getState().accessToken,
    getOrgId: () => useAuthStore.getState().selectedOrg?.id || organisationId,
  }), [organisationId]);
  
  const { apiClient: searchApiClient } = useGlobalSearchBridge({
    tokenProvider,
    onSettingsOpen: useCallback(() => setSettingsOpen(true), []),
  });

  const computedIsAdmin = useComputedIsAdmin(user as Record<string, any> | null);
  const { accessibleModules, permissions: rbacPermissions, hierarchyLevel } = useRBAC();

  // Map auth user to UI user format - includes ALL admin-related fields
  const uiUser: User & { displayEmail?: string } | null = user ? {
    id: user.id,
    name: user.name || user.full_name || user.email,
    email: user.email,
    // Header-only authority display; login identity stays on `email`.
    displayEmail: user.display_email || user.authority_email || user.email,
    organisation: user.organisation_name,
    organisationId: user.organisation_id,
    role: user.role,
    isAdmin: computedIsAdmin,
    admin: (user as Record<string, unknown>).admin as boolean | undefined,
    is_admin: (user as Record<string, unknown>).is_admin as boolean | undefined,
    is_staff: (user as Record<string, unknown>).is_staff as boolean | undefined,
    is_superuser: (user as Record<string, unknown>).is_superuser as boolean | undefined,
  } : null;

  // Build settings tabs using universal helper
  const settingsTabs = useMemo(() => buildSettingsTabs({
    user: user ? {
      id: user.id,
      first_name: user.first_name || user.name?.split(' ')[0],
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' '),
      email: user.email,
      organisation_name: user.organisation_name || organisationName,
      role: user.role,
      isAdmin: computedIsAdmin,
      permissions: rbacPermissions, hierarchy_level: hierarchyLevel,
    } : null,
    hubId: 'billing',
    hubName: 'Billing Hub',
    apiClient,
    organisationId: organisationId || user?.organisation_id,
    profileData: {
      first_name: user?.first_name || user?.name?.split(' ')[0],
      last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' '),
      email: user?.email,
      organisation_name: user?.organisation_name || organisationName,
    },
    organisationData: {
      id: organisationId || user?.organisation_id || '',
      name: organisationName || user?.organisation_name || 'Organisation',
    },
    integrations: [
      {
        id: 'xero',
        name: 'Xero',
        description: 'Accounting & invoicing platform',
        icon: '📊',
        connected: true, // Billing uses Xero by default
        status: 'connected',
        configUrl: 'https://dashboard.quotech.ai/settings?tab=integrations',
      },
    ],
  }), [user, organisationId, organisationName, apiClient, computedIsAdmin, rbacPermissions, hierarchyLevel]);
  
  // Update SECONDARY_NAV_ITEMS to use action for settings
  const secondaryNavWithSettingsAction = useMemo(() => 
    SECONDARY_NAV_ITEMS.map(item => 
      item.id === 'settings' 
        ? { ...item, href: undefined, action: () => setSettingsOpen(true) }
        : item
    ), []
  );

  // ── Tab navigation ─────────────────────────────────────
  const tabNav = useTabNavigation(BILLING_TAB_CONFIG);
  useTabKeyboardShortcuts({ useStore: useBillingTabStore, ...tabNav });

  return (
    <DashboardLayout
      navItems={NAV_ITEMS}
      secondaryNavItems={secondaryNavWithSettingsAction}
      externalLinks={EXTERNAL_LINKS}
      module={MODULE_CONFIG}
      accessibleModules={accessibleModules}
      user={uiUser}
      onLogout={logout}
      breadcrumbs={breadcrumbs}
      showGlobalSearch={true}
      globalSearchApiClient={searchApiClient}
      showCommandPalette
      commands={COMMANDS}
      hubLinks={HUB_LINKS}
      showOrgSwitcher
      showSettingsModal
      settingsTabs={settingsTabs}
      settingsModalOpen={settingsOpen}
      onCloseSettings={() => setSettingsOpen(false)}
      onOpenSettings={() => setSettingsOpen(true)}
      headerTabsSlot={
        <TabBar
          variant="inline"
          tabs={tabNav.tabItems}
          activeTabId={tabNav.activeTabId}
          onTabClick={tabNav.handleTabClick}
          onTabClose={tabNav.handleTabClose}
          onNewTab={tabNav.handleNewTab}
          onTabPin={tabNav.handleTabPin}
          onCloseOthers={tabNav.handleCloseOthers}
          onDuplicateTab={tabNav.handleDuplicateTab}
          accentColor="bg-orange-500"
        />
      }
    />
  );
}

function CfoReconciliationRedirect() {
  const url = import.meta.env.DEV
    ? 'http://localhost:5173/reconciliation'
    : 'https://cfo.quotech.ai/reconciliation'
  window.location.replace(url)
  return null
}

function OrganisationSettingsRedirect() {
  window.location.replace('https://dashboard.quotech.ai/settings?tab=integrations')
  return null
}

function App() {
  // Session monitoring (unified across all hubs)
  useEffect(() => {
    startSessionMonitoring({
      onTokenRefreshed: (newToken) => {
        
        // CRITICAL: Update Zustand store with the new token from SessionManager
        const refreshToken = getRefreshCookie()
        useAuthStore.getState().updateTokens(newToken, refreshToken || undefined)
        
        syncIntegrationStatus(newToken).catch(() => { /* silent: background sync */ })
      },
    });
    syncIntegrationStatus().catch(() => { /* silent: background sync */ });
    return () => stopSessionMonitoring();
  }, []);

  return (
    <ErrorBoundary>
      {/* Auto-update notifier — see qf!2578 for context (30s poll, 10-min idle auto-reload). */}
      <VersionWatcher pollIntervalMs={30_000} autoReloadAfterMs={null} />
      {/* Legacy SSO Handler */}
      <SSOHandler defaultRedirect="/book_keeping" onError={(error) => { if (import.meta.env.DEV) console.error('SSO Error:', error) }} />

      <Routes>
        {/* Unified SSO Route */}
        <Route path="/sso" element={
          <UnifiedSSOPage defaultRedirectPath="/book_keeping" loadingComponent={
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
              <div className="text-center">
                <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="mt-3 text-sm text-slate-500">Signing you in to Billing...</p>
              </div>
            </div>
          } />
        } />
        <Route path="/sso-callback" element={<SSOPage />} />

        {/* Protected routes with unified layout */}
        <Route path="/" element={<ProtectedRoute useMasterLogin><HubGuard hub="billing"><BillingLayout /></HubGuard></ProtectedRoute>}>
          <Route index element={<Navigate to="/book_keeping" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="reconciliation" element={<CfoReconciliationRedirect />} />
          <Route path="inbox" element={<Navigate to="/book_keeping" replace />} />
          <Route path="bills" element={<BillsPage />} />
          <Route path="bills/:id" element={<BillDetailPage />} />
          <Route path="book_keeping" element={<BookkeepingPage />} />
          <Route path="bookkeeping" element={<Navigate to="/book_keeping" replace />} />
          {/* Cash-out / payment schedule — AP calendar + bucketed list.
              Click a row → /book_keeping?id=<billId> opens it in the
              workspace for edit/approve. */}
          <Route path="payment_schedule" element={<PaymentSchedulePage />} />
          <Route path="schedule" element={<Navigate to="/payment_schedule" replace />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="automation" element={<Navigate to="/book_keeping" replace />} />
          <Route path="integrations" element={<OrganisationSettingsRedirect />} />
        </Route>

        <Route path="*" element={<Navigate to="/book_keeping" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;

